import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';
import { OverlayView } from '@react-google-maps/api';
import Map from './Map';
import CloseIcon from '../../images/icon-close.svg';
import { dummyUserImage } from '../../utils/system';

// Stable module-level constants so the controlled `center`/`zoom` props never
// change after mount — that is what previously caused the map to hard-snap
// (teleport) and to reset the user's manual pan on every re-render.
const DEFAULT_CENTER = { lat: 20, lng: 0 };
const DEFAULT_ZOOM = 2;
const FOCUS_ZOOM = 6;
const FIT_BOUNDS_PADDING = 64;

// The wrapper sits on the map point with the 40px avatar shifted so the point
// lands on its bottom centre. Only the avatar scales on hover: the card is a
// child of this wrapper now, and scaling the wrapper would scale the card too.
const MemberMarkerWrapper = styled.div`
  position: absolute;
  transform: translate(-20px, -40px);
  cursor: pointer;
  &:hover > img {
    transform: scale(1.1);
  }
`;

const ProfileImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  transition: transform 0.2s;
`;

// Holds the card above the avatar. The 15px gap between the two is padding
// rather than empty space, so the pointer never crosses an unhovered region on
// its way from avatar to card — across a real gap the card would flicker out
// mid-travel and could never be reached.
const InfoWindowAnchor = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  padding-bottom: 15px;
`;

const InfoWindowWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background: #FFF;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  min-width: 220px;
  max-width: 280px;
  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid white;
    transform: translateX(-50%);
  }
`;

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: '8px' }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', minWidth: 70, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

function MemberInfoWindow({ member }) {
  const hasDetails = member.department || member.email || member.phone || member.location;
  return (
    <InfoWindowWrapper>
      <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <ProfileImage
          src={member.profilePicture}
          alt={`${member.firstName} ${member.lastName}`}
          onError={(e) => {
            if (e.target.src !== dummyUserImage) {
              e.target.src = dummyUserImage;
            }
          }}
        />
        <Box>
          <Typography variant="subtitle1" fontWeight="600" lineHeight={1.2}>
            {member.firstName}
            {' '}
            {member.lastName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {member.designation || 'Team Member'}
          </Typography>
        </Box>
      </Box>

      {hasDetails && (
        <Box
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: '1px solid #eee',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <DetailRow label="Department" value={member.department} />
          <DetailRow label="Email" value={member.email} />
          <DetailRow label="Phone" value={member.phone} />
          <DetailRow label="Location" value={member.location} />
        </Box>
      )}
    </InfoWindowWrapper>
  );
}

function MemberMarker({
  member, onSelect, onHoverStart, onHoverEnd, isOpen,
}) {
  const detachRef = useRef(null);

  // Markers render into the map's OVERLAY_MOUSE_TARGET pane, so their DOM events
  // bubble up to the map and fire its onClick — which clears the selection we just
  // made, so the info window never stays open. Stopping only the high-level `click`
  // isn't enough: Google synthesizes the map's click from the low-level
  // mousedown/mouseup/pointer/touch events, so we stop those at the marker too.
  // These are native listeners (not React's, which are delegated above the map and
  // would fire too late to stop Google). Selection still happens on `click`.
  //
  // Hover is read from the same node but deliberately NOT swallowed: the map needs
  // its own pointer movement to keep panning and cursor handling working.
  //
  // This MUST be a callback ref rather than a mount effect reading a ref.
  // OverlayView renders null until Google hands it a map pane, so the marker node
  // appears a render *after* this component mounts: an effect would find an empty
  // ref and, with stable deps, never run again — which is exactly how every marker
  // ended up with no listeners at all, dead to both click and hover.
  const bindMarker = useCallback((node) => {
    if (detachRef.current) {
      detachRef.current();
      detachRef.current = null;
    }
    if (!node) return;

    const select = (event) => {
      event.stopPropagation();
      onSelect(member);
    };
    const enter = () => onHoverStart(member);
    const leave = () => onHoverEnd(member);
    const swallow = (event) => event.stopPropagation();
    const swallowed = [
      'pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'dblclick',
    ];

    node.addEventListener('click', select);
    node.addEventListener('mouseenter', enter);
    node.addEventListener('mouseleave', leave);
    swallowed.forEach((type) => node.addEventListener(type, swallow));

    detachRef.current = () => {
      node.removeEventListener('click', select);
      node.removeEventListener('mouseenter', enter);
      node.removeEventListener('mouseleave', leave);
      swallowed.forEach((type) => node.removeEventListener(type, swallow));
    };
  }, [onSelect, onHoverStart, onHoverEnd, member]);

  return (
    <OverlayView
      position={{ lat: member.lat, lng: member.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      {/* An open marker is raised above its neighbours so the card is never
          overlapped by adjacent profile images. The avatar stays rendered
          underneath the card: the card hangs off it as a child, which is what
          keeps the pointer inside this element — and the card open — while the
          cursor travels from avatar to card. */}
      <MemberMarkerWrapper
        ref={bindMarker}
        data-member-marker=""
        data-testid={`marker-${member.id}`}
        style={{ zIndex: isOpen ? 1000 : 1 }}
      >
        <ProfileImage
          src={member.profilePicture}
          alt={`${member.firstName} ${member.lastName}`}
          onError={(e) => {
            if (e.target.src !== dummyUserImage) {
              e.target.src = dummyUserImage;
            }
          }}
        />
        {isOpen && (
          <InfoWindowAnchor>
            <MemberInfoWindow member={member} />
          </InfoWindowAnchor>
        )}
      </MemberMarkerWrapper>
    </OverlayView>
  );
}

const CloseBtn = styled.div`
  background: white;
  filter: drop-shadow(0px 0.864px 3.458px rgba(0, 0, 0, 0.25));
  border-radius: 100px;
  padding: 10px;
  position: absolute;
  right: 33px;
  top: 33px;
  cursor: pointer;
`;

function MembersMap({ members, handleClose }) {
  const mapRef = useRef(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [hoveredMember, setHoveredMember] = useState(null);

  // Frame the map around the loaded members. Runs on map load and whenever the
  // member set changes (data arrives asynchronously, so this is what actually
  // centers the map — a one-time useState initializer could not).
  const fitToMembers = useCallback(() => {
    const map = mapRef.current;
    if (!map || members.length === 0) return;

    if (members.length === 1) {
      map.setCenter({ lat: members[0].lat, lng: members[0].lng });
      map.setZoom(FOCUS_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    members.forEach((member) => bounds.extend({ lat: member.lat, lng: member.lng }));
    map.fitBounds(bounds, FIT_BOUNDS_PADDING);
  }, [members]);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    fitToMembers();
  }, [fitToMembers]);

  useEffect(() => {
    fitToMembers();
  }, [fitToMembers]);

  // Stable so MemberMarker's native listeners aren't torn down and re-attached
  // on every selection or hover change.
  const handleMarkerClick = useCallback((member) => {
    setSelectedMember(member.id);
    const map = mapRef.current;
    if (!map) return;
    // Smooth animated pan instead of re-centering via a controlled prop.
    map.panTo({ lat: member.lat, lng: member.lng });
    if (map.getZoom() < FOCUS_ZOOM) {
      map.setZoom(FOCUS_ZOOM);
    }
  }, []);

  const handleMarkerHoverStart = useCallback((member) => setHoveredMember(member.id), []);
  const handleMarkerHoverEnd = useCallback(() => setHoveredMember(null), []);

  // Only a click on the bare map dismisses the card. Markers stopPropagation so
  // this should not see their clicks at all, but that depends on how Google routes
  // events out of the overlay pane; asking the DOM where the click landed does
  // not, so the card cannot be dismissed by the very click that opened it.
  const handleMapClick = useCallback((event) => {
    const target = event?.domEvent?.target;
    if (target?.closest?.('[data-member-marker]')) return;
    setSelectedMember(null);
  }, []);

  // Hovering previews a member; clicking pins one so the card survives the
  // pointer leaving. Hover wins while it lasts, so only ever one card is open —
  // a pinned card and a hovered one at the same time reads as a glitch.
  const openMemberId = hoveredMember ?? selectedMember;

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <Map
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        onLoad={handleMapLoad}
        onClick={handleMapClick}
      >
        {members.map((member) => (
          <MemberMarker
            key={`member-${member.id}`}
            member={member}
            onSelect={handleMarkerClick}
            onHoverStart={handleMarkerHoverStart}
            onHoverEnd={handleMarkerHoverEnd}
            isOpen={openMemberId === member.id}
          />
        ))}
        <CloseBtn onClick={handleClose} data-testid="close-btn">
          <img src={CloseIcon} alt="close icon" />
        </CloseBtn>
      </Map>
    </Box>
  );
}

MembersMap.propTypes = {
  members: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      profilePicture: PropTypes.string.isRequired,
      designation: PropTypes.string,
      department: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      location: PropTypes.string,
    }),
  ).isRequired,
  handleClose: PropTypes.func.isRequired,
};

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

DetailRow.defaultProps = {
  value: '',
};

MemberInfoWindow.propTypes = {
  member: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    profilePicture: PropTypes.string.isRequired,
    designation: PropTypes.string,
    department: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
};

MemberMarker.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    profilePicture: PropTypes.string.isRequired,
    designation: PropTypes.string,
    department: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onHoverStart: PropTypes.func.isRequired,
  onHoverEnd: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default MembersMap;
